/* eslint-disable @typescript-eslint/no-empty-function */
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';
import { SplashScreen } from '@capacitor/splash-screen';

import { DOCUMENT as DOC } from '@angular/common';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { CookiesService } from '@feature/analytics';
import { BrandingService } from '@feature/branding';
import { FreshchatService, FreshdeskService } from '@feature/freshdesk';
import { SharedAppUpdateService, SharedRootDetectionService } from '@feature/shared';
import { NavController } from '@ionic/angular';
import { EnvironmentService } from '@util/environment';

@Component({
  selector: 'shell-root',
  templateUrl: 'app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly freshdeskService: FreshdeskService = inject(FreshdeskService);
  private readonly freshchatService: FreshchatService = inject(FreshchatService);
  private readonly doc: Document = inject<Document>(DOC);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly navCtrl: NavController = inject(NavController);
  private readonly env: EnvironmentService = inject(EnvironmentService);
  private readonly cookiesService: CookiesService = inject(CookiesService);
  private readonly brandingService: BrandingService = inject(BrandingService);
  private readonly sharedAppUpdateService: SharedAppUpdateService = inject(SharedAppUpdateService);
  private readonly sharedRootDetectionService: SharedRootDetectionService = inject(SharedRootDetectionService);

  constructor() {
    this.listenToKeyboardChanges();
  }

  ngOnInit(): void {
    if (this.brandingService.branding().support === 'freshdesk') {
      this.freshdeskService.initFreshdeskScripts(this.renderer, this.doc);
    }

    if (this.brandingService.branding().freshchat) {
      this.freshchatService.initFreshchatScripts(this.renderer, this.doc);
    }

    this.brandingService.initBranding();

    this.listenForNavigationEventsForHeidiIntegration();

    this.cookiesService.openOverlayIfRequired();

    SplashScreen.hide();

    this.sharedAppUpdateService.checkAppStoreUpdates();

    this.sharedRootDetectionService.init();
  }

  ngOnDestroy(): void {
    if (Capacitor.isPluginAvailable('Keyboard')) {
      Keyboard.removeAllListeners();
    }
  }

  private listenForNavigationEventsForHeidiIntegration(): void {
    window.addEventListener(
      'message',
      (event: MessageEvent<any>) => {
        // Needed for HEIDI widget to navigate to the providers page
        if (
          event.origin === this.env.environment.iframes.heidiOrigin &&
          event.data &&
          event.data.type === 'navigate' &&
          event.data.target === 'providers'
        ) {
          this.navCtrl.navigateRoot(['tabs', 'clinics'], { queryParams: { ...(event.data.queryParams ?? {}) } });
        }

        // Needed for HEIDI Health widget to open a browser to start video calling
        if (
          event.origin === this.env.environment.iframes.heidiOrigin &&
          event.data &&
          event.data.type === 'open_browser' &&
          event.data.target
        ) {
          Browser.open({
            url: event.data.target,
          });
        }
      },
      false,
    );
  }

  private listenToKeyboardChanges(): void {
    if (Capacitor.isPluginAvailable('Keyboard')) {
      Keyboard.addListener('keyboardWillShow', this.setClass);
      Keyboard.addListener('keyboardWillHide', this.removeClass);
    }
  }

  private readonly setClass = () => document.body.classList.add('keyboard-shown');
  private readonly removeClass = () => document.body.classList.remove('keyboard-shown');
}
